import { useState } from 'react';
import { Field } from '@/components/Field';
import { SRI_LANKA_DISTRICTS } from '@/config/surveyOptions';
import { useGeolocation, type GeolocationFix } from '@/hooks/useGeolocation';
import { getDistrictFromCoordinates } from '@/services/districtLookup';

export interface LocationValues {
  latitude: string;
  longitude: string;
  gpsAccuracy: string;
  district: string;
  manualLocation: string;
}

interface LocationPickerProps {
  values: LocationValues;
  errors: Partial<Record<keyof LocationValues, string>>;
  onChange: (changes: Partial<LocationValues> & { gpsCapturedAt?: number }) => void;
  disabled?: boolean;
}

/**
 * GPS capture + district detection, with a manual override for everything.
 *
 * A denied permission or a failed lookup never blocks the form: the surveyor
 * can always type the coordinates and pick the district by hand.
 */
export function LocationPicker({ values, errors, onChange, disabled }: LocationPickerProps) {
  const { status, message, requestLocation } = useGeolocation();
  const [districtNote, setDistrictNote] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);

  const applyFix = async (fix: GeolocationFix) => {
    onChange({
      latitude: fix.latitude.toFixed(6),
      longitude: fix.longitude.toFixed(6),
      gpsAccuracy: Math.round(fix.accuracy).toString(),
      gpsCapturedAt: fix.capturedAt,
    });
    await detectDistrict(fix.latitude, fix.longitude);
  };

  const detectDistrict = async (latitude: number, longitude: number) => {
    setDetecting(true);
    setDistrictNote(null);
    try {
      const district = await getDistrictFromCoordinates(latitude, longitude);
      if (district) {
        onChange({ district });
        setDistrictNote(
          `District detected from GPS: ${district}. You can change it if it is wrong.`,
        );
      } else {
        setDistrictNote(
          'These coordinates are outside every district boundary. Select the district manually.',
        );
      }
    } finally {
      setDetecting(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    const fix = await requestLocation();
    if (fix) await applyFix(fix);
  };

  const handleDetectFromTypedCoordinates = async () => {
    const latitude = Number(values.latitude);
    const longitude = Number(values.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setDistrictNote('Enter a valid latitude and longitude first.');
      return;
    }
    await detectDistrict(latitude, longitude);
  };

  const locating = status === 'locating';
  const gpsFailed =
    status === 'denied' ||
    status === 'insecure' ||
    status === 'unavailable' ||
    status === 'timeout' ||
    status === 'error';

  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          className="btn-secondary w-full"
          onClick={handleUseCurrentLocation}
          disabled={disabled || locating}
        >
          {locating ? 'Finding location…' : 'Use Current Location'}
        </button>
        {message ? (
          <p
            className={`mt-2 text-sm ${gpsFailed ? 'font-medium text-amber-800' : 'text-slate-600'}`}
            role="status"
          >
            {message}
          </p>
        ) : null}
        {gpsFailed ? (
          <p className="mt-1 text-sm text-slate-600">
            GPS could not be detected. Enter the location details below by hand — the survey can
            continue normally.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field id="latitude" label="Latitude" error={errors.latitude}>
          <input
            id="latitude"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="6.927079"
            className={`text-input ${errors.latitude ? 'text-input-invalid' : ''}`}
            value={values.latitude}
            disabled={disabled}
            aria-invalid={Boolean(errors.latitude)}
            aria-errormessage={errors.latitude ? 'latitude-error' : undefined}
            onChange={(event) => onChange({ latitude: event.target.value })}
          />
        </Field>
        <Field id="longitude" label="Longitude" error={errors.longitude}>
          <input
            id="longitude"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="79.861244"
            className={`text-input ${errors.longitude ? 'text-input-invalid' : ''}`}
            value={values.longitude}
            disabled={disabled}
            aria-invalid={Boolean(errors.longitude)}
            aria-errormessage={errors.longitude ? 'longitude-error' : undefined}
            onChange={(event) => onChange({ longitude: event.target.value })}
          />
        </Field>
      </div>

      <Field
        id="gpsAccuracy"
        label="GPS Accuracy (m)"
        error={errors.gpsAccuracy}
        hint="Filled in automatically when GPS is used."
      >
        <input
          id="gpsAccuracy"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="8"
          className={`text-input ${errors.gpsAccuracy ? 'text-input-invalid' : ''}`}
          value={values.gpsAccuracy}
          disabled={disabled}
          onChange={(event) => onChange({ gpsAccuracy: event.target.value })}
        />
      </Field>

      <Field
        id="district"
        label="District"
        required
        error={errors.district}
        hint={districtNote ?? 'Detected from GPS using offline Sri Lanka district boundaries.'}
      >
        <div className="flex gap-2">
          <select
            id="district"
            className={`text-input ${errors.district ? 'text-input-invalid' : ''}`}
            value={values.district}
            disabled={disabled}
            aria-invalid={Boolean(errors.district)}
            onChange={(event) => {
              onChange({ district: event.target.value });
              setDistrictNote(null);
            }}
          >
            <option value="">Select district…</option>
            {SRI_LANKA_DISTRICTS.map((district) => (
              <option key={district} value={district}>
                {district}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary shrink-0 px-3"
            onClick={handleDetectFromTypedCoordinates}
            disabled={disabled || detecting}
            title="Detect the district from the coordinates above"
          >
            {detecting ? '…' : 'Detect'}
          </button>
        </div>
      </Field>

      <Field
        id="manualLocation"
        label="Location Note"
        hint="Optional. A landmark or description, e.g. near the school junction."
      >
        <input
          id="manualLocation"
          type="text"
          autoComplete="off"
          className="text-input"
          value={values.manualLocation}
          disabled={disabled}
          maxLength={200}
          onChange={(event) => onChange({ manualLocation: event.target.value })}
        />
      </Field>
    </div>
  );
}
